import { useState, useContext } from "react";
import { useNavigate, Link } from "react-router-dom";
import { AuthContext, API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Globe } from "lucide-react";
import { useSiteContent } from "@/context/SiteContentContext";

const Login = () => {
  const navigate = useNavigate();
  const { login } = useContext(AuthContext);
  const { getContent } = useSiteContent();
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });
  const [loading, setLoading] = useState(false);

  // Use XMLHttpRequest to avoid body stream issues from fetch interceptors
  const apiRequest = (url, options) => {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open(options.method || 'GET', url, true);
      
      // Set headers
      if (options.headers) {
        Object.keys(options.headers).forEach(key => {
          xhr.setRequestHeader(key, options.headers[key]);
        });
      }
      
      xhr.timeout = 10000;
      
      xhr.onload = () => {
        try {
          const data = JSON.parse(xhr.responseText);
          resolve({ ok: xhr.status >= 200 && xhr.status < 300, status: xhr.status, data });
        } catch (e) {
          resolve({ ok: false, status: xhr.status, data: { detail: 'Invalid server response' } });
        }
      };
      
      xhr.onerror = () => reject(new Error('Network error'));
      xhr.ontimeout = () => reject(new Error('Request timed out'));
      
      xhr.send(options.body || null);
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const { ok, status, data } = await apiRequest(`${API}/auth/login`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });

      if (ok) {
        login(data.token, data.user);
        toast.success("Login successful!");
        
        if (data.user.role === "interpreter") {
          navigate("/interpreter/dashboard");
        } else if (data.user.role === "admin") {
          navigate("/admin/dashboard");
        } else {
          navigate("/user/dashboard");
        }
      } else {
        toast.error(data.detail || "Login failed. Please check your credentials.");
      }
    } catch (error) {
      console.error("Login error:", error);
      toast.error(error.message || "An error occurred. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-6 py-12">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-2 mb-4">
            <Globe className="w-10 h-10 text-slate-900" />
            <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>Book an Interpreter</h1>
          </div>
          <h2 className="text-2xl font-semibold text-slate-900" style={{ fontFamily: 'Manrope' }}>{getContent("login", "title", "Welcome Back")}</h2>
          <p className="text-slate-600 mt-2" style={{ fontFamily: 'Public Sans' }}>{getContent("login", "subtitle", "Sign in to your account")}</p>
        </div>

        <div className="card p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <Label htmlFor="email" className="text-slate-700 font-medium">Email Address</Label>
              <Input
                id="email"
                data-testid="login-email-input"
                type="email"
                required
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="mt-2 h-11 rounded-md border-slate-200 focus:ring-2 focus:ring-slate-900/20 focus:border-slate-900"
                placeholder="you@example.com"
              />
            </div>

            <div>
              <div className="flex justify-between items-center">
                <Label htmlFor="password" className="text-slate-700 font-medium">Password</Label>
                <Link 
                  to="/forgot-password" 
                  className="text-sm text-slate-600 hover:text-slate-900 hover:underline"
                  data-testid="forgot-password-link"
                >
                  Forgot password?
                </Link>
              </div>
              <Input
                id="password"
                data-testid="login-password-input"
                type="password"
                required
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                className="mt-2 h-11 rounded-md border-slate-200 focus:ring-2 focus:ring-slate-900/20 focus:border-slate-900"
                placeholder="••••••••"
              />
            </div>

            <Button
              data-testid="login-submit-btn"
              type="submit"
              disabled={loading}
              className="w-full bg-slate-900 text-white hover:bg-slate-800 rounded-md py-6 font-semibold shadow-sm hover:shadow-md transition-all"
            >
              {loading ? "Logging in..." : "Log In"}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-slate-600" style={{ fontFamily: 'Public Sans' }}>
              Don't have an account?{" "}
              <Link to="/register" className="text-slate-900 font-semibold hover:underline">
                Register here
              </Link>
            </p>
          </div>
        </div>

        <div className="mt-6 text-center">
          <Link to="/" className="text-slate-600 hover:text-slate-900 transition-colors">
            ← Back to Home
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Login;